import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { lastKnownUser, rememberUser, forgetUser, setSessionExpired } from "../lib/authState";

const AuthContext = createContext(null);
let explicitSignOut = false;
export const markExplicitSignOut = () => { explicitSignOut = true; forgetUser(); };

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [known, setKnown] = useState(lastKnownUser);
  const [loading, setLoading] = useState(() => !lastKnownUser());

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      if (data.session?.user) { rememberUser(data.session.user); setKnown(lastKnownUser()); }
      setSession(data.session); setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === "SIGNED_OUT") {
        if (explicitSignOut) { explicitSignOut = false; setKnown(null); setSession(null); setSessionExpired(false); }
        // implicit sign-out (refresh failure / offline): keep last-known user so cached views keep working
        else if (navigator.onLine) setSessionExpired(true);
        return;
      }
      if (next?.user) { rememberUser(next.user); setKnown(lastKnownUser()); setSessionExpired(false); }
      setSession(next);
    });
    return () => { mounted = false; subscription.unsubscribe(); };
  }, []);

  const cachedUser = useMemo(() => (known ? { id: known.id, email: known.email, user_metadata: { full_name: known.name }, cached: true } : null), [known]);
  const user = session?.user ?? cachedUser;
  return (
    <AuthContext.Provider value={{ session, user, loading, cachedUser: !session && !!known }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
